import React from 'react';
import { toFlag } from 'pano-utils';
import type { CFNextScene, CFPrevScene, Hotspot } from '@types';
import addComponent from '@utils/addComponent';
import { Collapse } from '@components/Collapse';
import { Dropdown } from '@components/Dropdown';
import { $sceneMap, $tourData } from '@stores/dataStores';
import Button from '@components/Button';
import { $loadedId } from '@stores/loadedStore';
import { useCustomFeature } from '@modules/panel/CustomFeatureContext';
import SwitchSettingModal from '@modules/panel/components/SwitchSettingModal';
import { switchTypeItems } from '@modules/panel/constants';
import FeatureToggle from '@components/FeatureToggle';
import { getNextSceneId, getPrevSceneId } from '@modules/category';

type Props = {
  hotspot?: Hotspot;
  reverse?: boolean;
};

/**
 * 上一场景/下一场景模块, 用于热点和图层
 */
function AdvNextSceneSetting({ hotspot, reverse }: Props) {
  const { data, setData } = useCustomFeature<CFNextScene | CFPrevScene>();

  const handleSwitchSettingClick = () => {
    const tourData = $tourData.getState();
    const map = $sceneMap.getState();
    const sceneId = $loadedId.getState()!;
    const nextId = reverse
      ? getPrevSceneId(sceneId, {
          ignoreHidden: data.skipThumbHidden === 1,
        })
      : getNextSceneId(sceneId, {
          ignoreHidden: data.skipThumbHidden === 1,
        });
    if (!nextId) return;
    addComponent((remove) => {
      return (
        <SwitchSettingModal
          exitScene={map.get(sceneId)!}
          enterScene={map.get(nextId)!}
          dragMode={tourData.flag?.dragMode}
          hotspot={hotspot}
          data={data}
          onConfirm={(settings) => {
            setData((draft) => {
              draft.exitTargetView = settings.exitTargetView;
              draft.enterInitView = settings.enterInitView;
              draft.enterTargetView = settings.enterTargetView;
              draft.animation = settings.animation;
            });
          }}
          onRequestClose={remove}
        />
      );
    });
  };

  return (
    <Collapse title="场景切换效果">
      <Dropdown
        items={switchTypeItems}
        selected={data.animation ?? 0}
        onChange={(val) => {
          setData((draft) => {
            draft.animation = val;
          });
        }}
      />
      <FeatureToggle
        title="切换时跳过隐藏缩略图场景"
        isOn={!!data.skipThumbHidden}
        onToggle={(bool) => {
          setData((draft) => {
            draft.skipThumbHidden = toFlag(bool);
          });
        }}
      />
      <FeatureToggle
        title={reverse ? '切换至首场景不再切换' : '切换至最后场景不再切换'}
        isOn={!!data.stopInLast}
        onToggle={(bool) => {
          setData((draft) => {
            draft.stopInLast = toFlag(bool);
          });
        }}
      />
      <Button className="mt-20 w-full" onClick={handleSwitchSettingClick}>
        切换动画高级设置
      </Button>
    </Collapse>
  );
}

export default AdvNextSceneSetting;
